<div class="domain-management">
  <!-- Header with buttons -->
  <div class="d-flex justify-content-between align-items-center flex-wrap mb-3 gap-2">
    <h5 class="mb-0">
      <i class="bi bi-server me-2"></i>
      {{ 'SystemAdmin.domainManagement' | translate }}
    </h5>

    <div class="d-flex align-items-center gap-2">
      <a class="btn btn-outline-secondary btn-sm" routerLink="/system-admin/sql-editor"
         [attr.title]="'SystemAdmin.SqlQueryEditor.title' | translate">
        <i class="bi bi-database-gear me-1"></i>
        <span class="d-none d-md-inline">{{ 'SystemAdmin.SqlQueryEditor.title' | translate }}</span>
      </a>
      <button type="button" class="btn btn-action-dark btn-sm" (click)="openCreateModal()">
        <i class="bi bi-plus-circle me-1"></i>
        {{ 'SystemAdmin.addDomain' | translate }}
      </button>
    </div>
  </div>

  <!-- Smart Grid -->
  <div class="row" *ngIf="columns.length > 0">
    <div class="col-12">
      <konti-smart-grid 
        [data]="domains?.rows || []" 
        [columns]="columns" 
        [initialSort]="[{ field: 'domainName', dir: 'asc' }]"
        [showGroupPanel]="false">      
        <!-- Actions column -->
        <ng-template appSmartGridCell="actions" let-row>
          <div class="btn-group" ngbDropdown container="body">
            <button type="button" class="btn btn-sm btn-outline-dark dropdown-toggle" ngbDropdownToggle
                    [attr.aria-label]="'General.actions' | translate"
                    [attr.title]="'General.actions' | translate">
              <i class="bi bi-gear" aria-hidden="true"></i>
            </button>
            <div class="dropdown-menu dropdown-menu-end" ngbDropdownMenu>
              <button ngbDropdownItem (click)="openEditModal(row)">
                <i class="bi bi-pencil me-2 text-dark"></i>
                {{ 'General.edit' | translate }}
              </button>
              <button ngbDropdownItem (click)="openModuleModal(row)">
                <i class="bi bi-puzzle me-2 text-dark"></i>
                {{ 'SystemAdmin.configureModules' | translate }}
              </button>
              <button ngbDropdownItem (click)="openNotificationConfigModal(row)">
                <i class="bi bi-clock-history me-2 text-dark"></i>
                {{ 'SystemAdmin.scheduledJobsConfig' | translate }}
              </button>
              <button ngbDropdownItem (click)="openOAuthSettings(row)">
                <i class="bi bi-shield-lock me-2 text-dark"></i>
                {{ 'SystemAdmin.configureOAuth' | translate }}
              </button>
              <div class="dropdown-divider"></div>
              <button ngbDropdownItem (click)="copyExistingApiKey(row)" *ngIf="row.domainAPIKey">
                <i class="bi bi-clipboard me-2 text-dark"></i>
                {{ 'SystemAdmin.copyApiKey' | translate }}
              </button>
              <button ngbDropdownItem (click)="openRegenerateApiKeyModal(row)">
                <i class="bi bi-key me-2 text-dark"></i>
                {{ 'SystemAdmin.regenerateApiKey' | translate }}
              </button>
              <div class="dropdown-divider"></div>
              <button ngbDropdownItem (click)="openDeleteModal(row)" class="text-danger">
                <i class="bi bi-trash3 me-2"></i>
                {{ 'General.delete' | translate }}
              </button>
            </div>
          </div>
        </ng-template>
      </konti-smart-grid>
    </div>
  </div>
</div>

<!-- Domain Modal (Create/Edit) -->
<ng-template #domainModal let-modal>
  <div class="modal-header">
    <h5 class="modal-title">
      <i class="bi bi-server me-2"></i>
      {{ (isEditing ? 'SystemAdmin.editDomain' : 'SystemAdmin.addDomain') | translate }}
    </h5>
    <button type="button" class="btn-close" aria-label="Close" (click)="closeModal()"></button>
  </div>
  
  <div class="modal-body">
    <form [formGroup]="domainForm">
      <!-- Domain Name -->
      <div class="mb-3">
        <label class="form-label">
          {{ 'SystemAdmin.domainName' | translate }} <span class="text-danger">*</span>
        </label>
        <input type="text" class="form-control" formControlName="domainName" maxlength="100"
               placeholder="customer.eportal.no"
               [class.is-invalid]="f['domainName'].invalid && f['domainName'].touched" />
        <div class="invalid-feedback" *ngIf="f['domainName'].invalid && f['domainName'].touched">
          {{ 'General.required' | translate }}
        </div>
      </div>

      <!-- Database Configuration -->
      <h6 class="border-bottom pb-2 mb-3">{{ 'SystemAdmin.databaseConfiguration' | translate }}</h6>
      
      <div class="row">
        <div class="col-md-6">
          <div class="mb-3">
            <label class="form-label">
              {{ 'SystemAdmin.databaseName' | translate }} <span class="text-danger">*</span>
            </label>
            <input type="text" class="form-control" formControlName="databaseName" maxlength="30"
                   placeholder="wv_client_customer"
                   [class.is-invalid]="f['databaseName'].invalid && f['databaseName'].touched" />
            <div class="invalid-feedback" *ngIf="f['databaseName'].invalid && f['databaseName'].touched">
              {{ 'General.required' | translate }}
            </div>
          </div>
        </div>
        
        <div class="col-md-6">
          <div class="mb-3">
            <label class="form-label">
              {{ 'SystemAdmin.userAccount' | translate }} <span class="text-danger">*</span>
            </label>
            <ng-select 
              [items]="userAccounts"
              bindLabel="fullName"
              bindValue="userAccountID"
              formControlName="userAccountID"
              [placeholder]="'SystemAdmin.selectUserAccount' | translate"
              [class.is-invalid]="f['userAccountID'].invalid && f['userAccountID'].touched">
              <ng-template ng-option-tmp let-item="item">
                <div>
                  <strong>{{ item.fullName }}</strong>
                  <div class="text-muted small" *ngIf="item.email">{{ item.email }}</div>
                </div>
              </ng-template>
            </ng-select>
            <div class="invalid-feedback d-block" *ngIf="f['userAccountID'].invalid && f['userAccountID'].touched">
              {{ 'General.required' | translate }}
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-md-6">
          <div class="mb-3">
            <label class="form-label">
              {{ 'SystemAdmin.databaseUsername' | translate }} <span class="text-danger">*</span>
            </label>
            <input type="text" class="form-control" formControlName="databaseUsername" maxlength="30"
                   [class.is-invalid]="f['databaseUsername'].invalid && f['databaseUsername'].touched" />
            <div class="invalid-feedback" *ngIf="f['databaseUsername'].invalid && f['databaseUsername'].touched">
              {{ 'General.required' | translate }}
            </div>
          </div>
        </div>
        
        <div class="col-md-6">
          <div class="mb-3">
            <label class="form-label">
              {{ 'SystemAdmin.databasePassword' | translate }} <span class="text-danger">*</span>
            </label>
            <input type="password" class="form-control" formControlName="databasePassword" maxlength="250"
                   [class.is-invalid]="f['databasePassword'].invalid && f['databasePassword'].touched" />
            <div class="invalid-feedback" *ngIf="f['databasePassword'].invalid && f['databasePassword'].touched">
              {{ 'General.required' | translate }}
            </div>
          </div>
        </div>
      </div>

      <!-- Optional: Customization -->
      <h6 class="border-bottom pb-2 mb-3">{{ 'SystemAdmin.customization' | translate }}</h6>
      
      <div class="mb-3">
        <label class="form-label">{{ 'SystemAdmin.domainColors' | translate }}</label>
        <input type="text" class="form-control" formControlName="domainColors" 
               placeholder="#007bff,#6c757d,#28a745,#dc3545,#ffc107" />
        <small class="text-muted">{{ 'SystemAdmin.domainColorsHint' | translate }}</small>
      </div>

      <div class="row">
        <div class="col-md-4">
          <div class="mb-3">
            <label class="form-label">{{ 'SystemAdmin.userLogo' | translate }}</label>
            <input type="text" class="form-control" formControlName="userLogo" 
                   placeholder="/assets/images/logo.png" />
          </div>
        </div>
        
        <div class="col-md-4">
          <div class="mb-3">
            <label class="form-label">{{ 'SystemAdmin.userLogoSmall' | translate }}</label>
            <input type="text" class="form-control" formControlName="userLogoSmall" 
                   placeholder="/assets/images/logo-small.png" />
          </div>
        </div>
        
        <div class="col-md-4">
          <div class="mb-3">
            <label class="form-label">{{ 'SystemAdmin.userBackground' | translate }}</label>
            <input type="text" class="form-control" formControlName="userBackground" 
                   placeholder="/assets/images/background.jpg" />
          </div>
        </div>
      </div>

      <!-- Work Order Configuration -->
      <h6 class="border-bottom pb-2 mb-3">{{ 'SystemAdmin.workOrderConfiguration' | translate }}</h6>
      
      <div class="row">
        <div class="col-md-6">
          <div class="form-check mb-3">
            <input type="checkbox" class="form-check-input" id="useExternalWorkOrders" formControlName="useExternalWorkOrders" />
            <label class="form-check-label" for="useExternalWorkOrders">
              {{ 'SystemAdmin.useExternalWorkOrders' | translate }}
            </label>
            <small class="form-text text-muted d-block">
              {{ 'SystemAdmin.useExternalWorkOrdersHelp' | translate }}
            </small>
          </div>
        </div>
        
        <div class="col-md-6">
          <div class="form-check mb-3">
            <input type="checkbox" class="form-check-input" id="useStockIntegration" formControlName="useStockIntegration" />
            <label class="form-check-label" for="useStockIntegration">
              {{ 'SystemAdmin.useStockIntegration' | translate }}
            </label>
            <small class="form-text text-muted d-block">
              {{ 'SystemAdmin.useStockIntegrationHelp' | translate }}
            </small>
          </div>
        </div>

        <div class="col-md-6">
          <div class="form-check mb-3">
            <input type="checkbox" class="form-check-input" id="useBugReport" formControlName="useBugReport" />
            <label class="form-check-label" for="useBugReport">
              {{ 'SystemAdmin.useBugReport' | translate }}
            </label>
            <small class="form-text text-muted d-block">
              {{ 'SystemAdmin.useBugReportHelp' | translate }}
            </small>
          </div>
        </div>

        <div class="col-md-6">
          <div class="form-check mb-3">
            <input type="checkbox" class="form-check-input" id="useGlobalSearch" formControlName="useGlobalSearch" />
            <label class="form-check-label" for="useGlobalSearch">
              {{ 'SystemAdmin.useGlobalSearch' | translate }}
            </label>
            <small class="form-text text-muted d-block">
              {{ 'SystemAdmin.useGlobalSearchHelp' | translate }}
            </small>
          </div>
        </div>
      </div>

      <h6 class="border-bottom pb-2 mb-3 mt-4">{{ 'SystemAdmin.monitoringConfiguration' | translate }}</h6>
      <div class="row g-3">
        <div class="col-md-6">
          <div class="form-check mb-3">
            <input type="checkbox" class="form-check-input" id="isMonitoringEnabled" formControlName="isMonitoringEnabled" />
            <label class="form-check-label" for="isMonitoringEnabled">
              {{ 'SystemAdmin.isMonitoringEnabled' | translate }}
            </label>
            <small class="form-text text-muted d-block">
              {{ 'SystemAdmin.isMonitoringEnabledHelp' | translate }}
            </small>
          </div>
        </div>
      </div>

      <!-- ERP Configuration -->
      <h6 class="border-bottom pb-2 mb-3 mt-4">{{ 'SystemAdmin.erpConfiguration' | translate }}</h6>
      <div class="row g-3">
        <div class="col-md-6">
          <div class="mb-3">
            <label class="form-label" for="erpClientNumber">
              {{ 'SystemAdmin.erpClientNumber' | translate }}
            </label>
            <input type="text" class="form-control" id="erpClientNumber" formControlName="erpClientNumber"
                   [placeholder]="'SystemAdmin.erpClientNumberPlaceholder' | translate" maxlength="50" />
            <small class="form-text text-muted d-block">
              {{ 'SystemAdmin.erpClientNumberHelp' | translate }}
            </small>
          </div>
        </div>
      </div>

      <!-- License / Quota -->
      <h6 class="border-bottom pb-2 mb-3 mt-4">{{ 'SystemAdmin.licenseQuota' | translate }}</h6>
      <div class="row g-3">
        <div class="col-md-6">
          <div class="mb-3">
            <label class="form-label" for="domainUsers">
              {{ 'SystemAdmin.domainUsers' | translate }}
            </label>
            <input type="number" class="form-control" id="domainUsers" formControlName="domainUsers"
                   min="0" [class.is-invalid]="f['domainUsers'].invalid && f['domainUsers'].touched" />
            <div class="invalid-feedback" *ngIf="f['domainUsers'].invalid && f['domainUsers'].touched">
              {{ 'General.required' | translate }}
            </div>
          </div>
        </div>
        <div class="col-md-6">
          <div class="mb-3">
            <label class="form-label" for="domainGroups">
              {{ 'SystemAdmin.domainGroups' | translate }}
            </label>
            <input type="number" class="form-control" id="domainGroups" formControlName="domainGroups"
                   min="0" [class.is-invalid]="f['domainGroups'].invalid && f['domainGroups'].touched" />
            <div class="invalid-feedback" *ngIf="f['domainGroups'].invalid && f['domainGroups'].touched">
              {{ 'General.required' | translate }}
            </div>
          </div>
        </div>
      </div>
    </form>
  </div>
  
  <div class="modal-footer">
    <button type="button" class="btn btn-secondary" (click)="closeModal()">
      {{ 'General.cancel' | translate }}
    </button>
    <button type="button" class="btn btn-primary" (click)="saveDomain()">
      <i class="bi bi-save me-1"></i>
      {{ 'General.save' | translate }}
    </button>
  </div>
</ng-template>

<!-- Module Configuration Modal -->
<ng-template #moduleModal let-modal>
  <div class="modal-header">
    <h5 class="modal-title">
      <i class="bi bi-puzzle me-2"></i>
      {{ 'SystemAdmin.configureModules' | translate }}: {{ currentDomain?.domainName }}
    </h5>
    <button type="button" class="btn-close" aria-label="Close" (click)="closeModal()"></button>
  </div>
  
  <div class="modal-body">
    <div class="table-responsive">
      <table class="table table-sm table-hover">
        <thead>
          <tr>
            <th style="width: 50px;">{{ 'SystemAdmin.active' | translate }}</th>
            <th>{{ 'SystemAdmin.moduleName' | translate }}</th>
            <th style="width: 80px;">{{ 'SystemAdmin.internet' | translate }}</th>
            <th style="width: 80px;">{{ 'SystemAdmin.intranet' | translate }}</th>
            <th style="width: 80px;">{{ 'SystemAdmin.extranet' | translate }}</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let module of availableModules" [class.table-active]="module.isActive">
            <td>
              <div class="form-check">
                <input class="form-check-input" type="checkbox" 
                       [checked]="module.isActive"
                       (change)="toggleModule(module)">
              </div>
            </td>
            <td>{{ module.moduleName }}</td>
            <td>
              <span class="badge" [ngClass]="module.internet ? 'badge-soft-success' : 'badge-soft-secondary'">
                {{ module.internet ? ('General.yes' | translate) : ('General.no' | translate) }}
              </span>
            </td>
            <td>
              <span class="badge" [ngClass]="module.intranet ? 'badge-soft-success' : 'badge-soft-secondary'">
                {{ module.intranet ? ('General.yes' | translate) : ('General.no' | translate) }}
              </span>
            </td>
            <td>
              <span class="badge" [ngClass]="module.extranet ? 'badge-soft-success' : 'badge-soft-secondary'">
                {{ module.extranet ? ('General.yes' | translate) : ('General.no' | translate) }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
  
  <div class="modal-footer">
    <button type="button" class="btn btn-secondary" (click)="closeModal()">
      {{ 'General.cancel' | translate }}
    </button>
    <button type="button" class="btn btn-primary" (click)="saveModules()">
      <i class="bi bi-save me-1"></i>
      {{ 'General.save' | translate }}
    </button>
  </div>
</ng-template>

<!-- Delete Confirmation Modal -->
<ng-template #deleteConfirmModal let-modal>
  <div class="modal-header">
    <h5 class="modal-title">
      <i class="bi bi-exclamation-triangle text-danger me-2"></i>
      {{ 'General.confirmDelete' | translate }}
    </h5>
    <button type="button" class="btn-close" aria-label="Close" (click)="closeModal()"></button>
  </div>
  
  <div class="modal-body">
    <p>{{ 'SystemAdmin.confirmDeleteDomain' | translate }}: <strong>{{ domainToDelete?.domainName }}</strong>?</p>
    <div class="alert alert-warning">
      <i class="bi bi-exclamation-triangle me-2"></i>
      {{ 'SystemAdmin.deleteDomainWarning' | translate }}
    </div>
  </div>
  
  <div class="modal-footer">
    <button type="button" class="btn btn-secondary" (click)="closeModal()">
      {{ 'General.cancel' | translate }}
    </button>
    <button type="button" class="btn btn-danger" (click)="confirmDelete()">
      <i class="bi bi-trash3 me-1"></i>
      {{ 'General.delete' | translate }}
    </button>
  </div>
</ng-template>

<!-- Scheduled Jobs Config Modal -->
<ng-template #notificationConfigModal let-modal>
  <div class="modal-header">
    <h5 class="modal-title">
      <i class="bi bi-clock-history me-2"></i>
      {{ 'SystemAdmin.scheduledJobsConfig' | translate }}
    </h5>
    <button type="button" class="btn-close" aria-label="Close" (click)="closeModal()"></button>
  </div>
  
  <div class="modal-body">
    <form [formGroup]="notificationConfigForm">
      <div class="mb-3">
        <label class="form-label">{{ 'SystemAdmin.domainName' | translate }}</label>
        <input type="text" class="form-control" [value]="selectedDomainForNotification?.domainName" readonly disabled>
      </div>

      <!-- Master toggle: Aktiver tidsstyrte jobber -->
      <fieldset class="form-section">
        <legend class="form-section-legend">{{ 'SystemAdmin.scheduledJobsMasterSection' | translate }}</legend>
        <div class="mb-3">
          <div class="form-check form-switch">
            <input class="form-check-input" type="checkbox" id="isActive" formControlName="isActive">
            <label class="form-check-label" for="isActive">
              {{ 'SystemAdmin.scheduledJobsActive' | translate }}
            </label>
          </div>
          <small class="text-muted">{{ 'SystemAdmin.scheduledJobsActiveHint' | translate }}</small>
        </div>
      </fieldset>

      <!-- Individual job toggles (only visible when master is ON) -->
      <fieldset class="form-section" *ngIf="notificationConfigForm.value.isActive">
        <legend class="form-section-legend">{{ 'SystemAdmin.scheduledJobTogglesSection' | translate }}</legend>

        <div class="mb-2">
          <div class="form-check form-switch">
            <input class="form-check-input" type="checkbox" id="enableNotifications" formControlName="enableNotifications">
            <label class="form-check-label" for="enableNotifications">
              {{ 'SystemAdmin.enableNotifications' | translate }}
            </label>
          </div>
        </div>

        <div class="mb-2">
          <div class="form-check form-switch">
            <input class="form-check-input" type="checkbox" id="enableTimeScheduler" formControlName="enableTimeScheduler">
            <label class="form-check-label" for="enableTimeScheduler">
              {{ 'SystemAdmin.enableTimeScheduler' | translate }}
            </label>
          </div>
        </div>

        <div class="mb-2">
          <div class="form-check form-switch">
            <input class="form-check-input" type="checkbox" id="enableIntegrations" formControlName="enableIntegrations">
            <label class="form-check-label" for="enableIntegrations">
              {{ 'SystemAdmin.enableIntegrations' | translate }}
            </label>
          </div>
        </div>

        <div class="mb-2">
          <div class="form-check form-switch">
            <input class="form-check-input" type="checkbox" id="enableCrmTaskReminders" formControlName="enableCrmTaskReminders">
            <label class="form-check-label" for="enableCrmTaskReminders">
              {{ 'SystemAdmin.enableCrmTaskReminders' | translate }}
            </label>
          </div>
        </div>

        <div class="mb-2">
          <div class="form-check form-switch">
            <input class="form-check-input" type="checkbox" id="enableCrmAssignmentReminders" formControlName="enableCrmAssignmentReminders">
            <label class="form-check-label" for="enableCrmAssignmentReminders">
              {{ 'SystemAdmin.enableCrmAssignmentReminders' | translate }}
            </label>
          </div>
        </div>

        <div class="mb-2">
          <div class="form-check form-switch">
            <input class="form-check-input" type="checkbox" id="enableCrmAgreementExpiry" formControlName="enableCrmAgreementExpiry">
            <label class="form-check-label" for="enableCrmAgreementExpiry">
              {{ 'SystemAdmin.enableCrmAgreementExpiry' | translate }}
            </label>
          </div>
        </div>

        <div class="mb-2">
          <div class="form-check form-switch">
            <input class="form-check-input" type="checkbox" id="enableChecklistReminders" formControlName="enableChecklistReminders">
            <label class="form-check-label" for="enableChecklistReminders">
              {{ 'SystemAdmin.enableChecklistReminders' | translate }}
            </label>
          </div>
        </div>

        <div class="mb-2">
          <div class="form-check form-switch">
            <input class="form-check-input" type="checkbox" id="enableDeviationReminders" formControlName="enableDeviationReminders">
            <label class="form-check-label" for="enableDeviationReminders">
              {{ 'SystemAdmin.enableDeviationReminders' | translate }}
            </label>
          </div>
        </div>

        <div class="mb-2">
          <div class="form-check form-switch">
            <input class="form-check-input" type="checkbox" id="enableGoalReminders" formControlName="enableGoalReminders">
            <label class="form-check-label" for="enableGoalReminders">
              {{ 'SystemAdmin.enableGoalReminders' | translate }}
            </label>
          </div>
        </div>

        <div class="mb-2">
          <div class="form-check form-switch">
            <input class="form-check-input" type="checkbox" id="enableRiskReviewReminders" formControlName="enableRiskReviewReminders">
            <label class="form-check-label" for="enableRiskReviewReminders">
              {{ 'SystemAdmin.enableRiskReviewReminders' | translate }}
            </label>
          </div>
        </div>

        <div class="mb-2">
          <div class="form-check form-switch">
            <input class="form-check-input" type="checkbox" id="enableBankReconciliation" formControlName="enableBankReconciliation">
            <label class="form-check-label" for="enableBankReconciliation">
              {{ 'SystemAdmin.enableBankReconciliation' | translate }}
            </label>
          </div>
        </div>
      </fieldset>

      <!-- Interval and last checked -->
      <fieldset class="form-section" *ngIf="notificationConfigForm.value.isActive">
        <legend class="form-section-legend">{{ 'SystemAdmin.scheduledJobsIntervalSection' | translate }}</legend>
        <div class="mb-3">
          <label class="form-label">
            {{ 'SystemAdmin.checkIntervalMinutes' | translate }} <span class="text-danger">*</span>
          </label>
          <input type="number" class="form-control" formControlName="checkIntervalMinutes" 
                 min="1" max="1440"
                 [class.is-invalid]="ncf['checkIntervalMinutes'].invalid && ncf['checkIntervalMinutes'].touched" />
          <small class="text-muted">{{ 'SystemAdmin.checkIntervalHint' | translate }}</small>
          <div class="invalid-feedback" *ngIf="ncf['checkIntervalMinutes'].invalid && ncf['checkIntervalMinutes'].touched">
            {{ 'General.required' | translate }}
          </div>
        </div>

        <div class="mb-3" *ngIf="currentNotificationConfig?.lastCheckedTime">
          <label class="form-label">{{ 'SystemAdmin.lastCheckedTime' | translate }}</label>
          <input type="text" class="form-control" [value]="currentNotificationConfig.lastCheckedTime | date:'dd.MM.yyyy HH:mm'" readonly disabled>
        </div>
      </fieldset>
    </form>
  </div>
  
  <div class="modal-footer">
    <button type="button" class="btn btn-secondary" (click)="closeModal()">
      {{ 'General.cancel' | translate }}
    </button>
    <button type="button" class="btn btn-primary" (click)="saveNotificationConfig()"
            [disabled]="notificationConfigForm.invalid">
      <i class="bi bi-save me-1"></i>
      {{ 'General.save' | translate }}
    </button>
  </div>
</ng-template>

<!-- API Key Display Modal -->
<ng-template #apiKeyModal let-modal>
  <div class="modal-header">
    <h5 class="modal-title">
      <i class="bi bi-key me-2"></i>
      {{ 'SystemAdmin.newApiKey' | translate }}
    </h5>
    <button type="button" class="btn-close" aria-label="Close" (click)="closeModal()"></button>
  </div>
  
  <div class="modal-body">
    <div class="alert alert-info">
      <i class="bi bi-info-circle me-2"></i>
      {{ 'SystemAdmin.apiKeyWarning' | translate }}
    </div>
    
    <div class="input-group">
      <input type="text" class="form-control font-monospace" [value]="newApiKey" readonly>
      <button class="btn btn-outline-secondary" type="button" (click)="copyApiKey()">
        <i class="bi bi-clipboard"></i>
      </button>
    </div>
  </div>
  
  <div class="modal-footer">
    <button type="button" class="btn btn-primary" (click)="closeModal()">
      {{ 'General.close' | translate }}
    </button>
  </div>
</ng-template>

<!-- API Key Regeneration Confirmation Modal -->
<ng-template #regenerateApiKeyConfirmModal let-modal>
  <div class="modal-header bg-warning">
    <h5 class="modal-title">
      <i class="bi bi-exclamation-triangle me-2"></i>
      {{ 'SystemAdmin.confirmRegenerateApiKey' | translate }}
    </h5>
    <button type="button" class="btn-close" aria-label="Close" (click)="closeRegenerateModal()"></button>
  </div>
  
  <div class="modal-body">
    <div class="alert alert-danger">
      <i class="bi bi-exclamation-octagon me-2"></i>
      <strong>{{ 'SystemAdmin.warning' | translate }}</strong>
      <p class="mb-0 mt-2">{{ 'SystemAdmin.regenerateApiKeyWarning' | translate }}</p>
    </div>
    
    <p class="mb-3">
      <strong>{{ 'SystemAdmin.domain' | translate }}:</strong> {{ domainToRegenerate?.domainName }}
    </p>
    
    <div class="form-check">
      <input class="form-check-input" type="checkbox" id="confirmRegenerate" [(ngModel)]="confirmRegenerateCheckbox">
      <label class="form-check-label fw-bold" for="confirmRegenerate">
        {{ 'SystemAdmin.iUnderstandApiKeyWillChange' | translate }}
      </label>
    </div>
  </div>
  
  <div class="modal-footer">
    <button type="button" class="btn btn-secondary" (click)="closeRegenerateModal()">
      {{ 'General.cancel' | translate }}
    </button>
    <button type="button" class="btn btn-danger" 
            [disabled]="!confirmRegenerateCheckbox"
            (click)="confirmRegenerateApiKey()">
      <i class="bi bi-key me-1"></i>
      {{ 'SystemAdmin.regenerate' | translate }}
    </button>
  </div>
</ng-template>
